import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
    Ship, 
    AlertTriangle, 
    Flame, 
    Phone, 
    Activity,
    Navigation,
    FileWarning,
    Clock,
    AlertCircle,
    Trophy,
    X
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';

const API = `${window.location.origin}/api`;

const COLORS = {
    cyan: '#06b6d4',
    emerald: '#10b981',
    amber: '#f59e0b',
    red: '#ef4444',
    slate: '#64748b'
};

const criticidadeColors = {
    baixa: COLORS.slate,
    media: COLORS.cyan,
    alta: COLORS.amber,
    critica: COLORS.red
};

const statusColors = {
    navegando: COLORS.emerald,
    atracado: COLORS.cyan,
    manutencao: COLORS.amber,
    emergencia: COLORS.red
};

const DashboardPage = () => {
    const [kpis, setKpis] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [convoys, setConvoys] = useState([]);
    const [recentRoos, setRecentRoos] = useState([]);
    const [alertasDocumentos, setAlertasDocumentos] = useState(null);
    const [alertasPLR, setAlertasPLR] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
        fetchAlertasPLR();
    }, []);

    const fetchAlertasPLR = async () => {
        try {
            const r = await axios.get(`${API}/operacao/desempenho-plr/alertas?apenas_nao_lidos=true`);
            setAlertasPLR(r.data.alertas || []);
        } catch (e) { /* noop */ }
    };

    const fetchData = async () => {
        try {
            const [kpisRes, chartRes, convoysRes, roosRes, alertasRes] = await Promise.all([
                axios.get(`${API}/dashboard/kpis`),
                axios.get(`${API}/dashboard/chart-data`),
                axios.get(`${API}/convoys`),
                axios.get(`${API}/roos`),
                axios.get(`${API}/alertas/documentos-vencimento?dias=30`)
            ]);
            
            setKpis(kpisRes.data);
            setChartData(chartRes.data);
            setConvoys(convoysRes.data.slice(0, 5));
            setRecentRoos(roosRes.data.slice(0, 5));
            setAlertasDocumentos(alertasRes.data);
        } catch (error) {
            console.error('Erro ao carregar dados:', error);
        } finally {
            setLoading(false);
        }
    };

    const marcarAlertaPLRLido = async (alertaId) => {
        try {
            await axios.post(`${API}/operacao/desempenho-plr/alertas/${alertaId}/marcar-lido`);
            setAlertasPLR(prev => prev.filter(a => a.id !== alertaId));
        } catch (e) { /* noop */ }
    };

    const formatChartData = (data, labelMap) => {
        if (!data) return [];
        return data.map(item => ({
            name: labelMap[item._id] || item._id || 'N/A',
            value: item.count
        }));
    };

    const criticidadeLabels = {
        baixa: 'Baixa',
        media: 'Média',
        alta: 'Alta',
        critica: 'Crítica'
    };

    const statusLabels = {
        navegando: 'Navegando',
        atracado: 'Atracado',
        manutencao: 'Manutenção',
        emergencia: 'Emergência'
    };

    const tipoSinistroLabels = {
        colisao: 'Colisão',
        encalhe: 'Encalhe',
        incendio: 'Incêndio',
        vazamento: 'Vazamento',
        acidente_pessoal: 'Acidente Pessoal',
        avaria: 'Avaria',
        outros: 'Outros'
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
            {/* Alertas de Documentos */}
            {alertasDocumentos && (alertasDocumentos.vencidos > 0 || alertasDocumentos.criticos > 0) && (
                <Card className="bg-red-950/30 border-red-900/50 rounded-sm">
                    <CardHeader className="border-b border-red-900/30 p-4">
                        <CardTitle className="font-heading text-lg text-red-400 flex items-center gap-2">
                            <FileWarning className="w-5 h-5" />
                            Alertas de Documentos
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                        <div className="flex flex-wrap gap-4 mb-4">
                            {alertasDocumentos.vencidos > 0 && (
                                <div className="flex items-center gap-2 px-3 py-2 bg-red-900/30 rounded-sm">
                                    <AlertCircle className="w-4 h-4 text-red-400" />
                                    <span className="text-red-400 font-mono text-sm">
                                        {alertasDocumentos.vencidos} documento(s) vencido(s)
                                    </span>
                                </div>
                            )}
                            {alertasDocumentos.criticos > 0 && (
                                <div className="flex items-center gap-2 px-3 py-2 bg-amber-900/30 rounded-sm">
                                    <Clock className="w-4 h-4 text-amber-400" />
                                    <span className="text-amber-400 font-mono text-sm">
                                        {alertasDocumentos.criticos} vence(m) em até 7 dias
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                            {alertasDocumentos.alertas.slice(0, 5).map((alerta) => (
                                <div 
                                    key={alerta.documento_id}
                                    className={`flex items-center justify-between p-2 rounded-sm ${
                                        alerta.status === 'vencido' ? 'bg-red-900/20' :
                                        alerta.status === 'critico' ? 'bg-amber-900/20' : 'bg-slate-800/50'
                                    }`}
                                >
                                    <div>
                                        <p className="text-sm text-white">{alerta.documento_nome}</p>
                                        <p className="text-xs text-slate-400">{alerta.embarcacao_nome}</p>
                                    </div>
                                    <div className="text-right">
                                        <span className={`text-xs font-mono ${
                                            alerta.status === 'vencido' ? 'text-red-400' :
                                            alerta.status === 'critico' ? 'text-amber-400' : 'text-slate-400'
                                        }`}>
                                            {alerta.dias_restantes < 0 
                                                ? `Vencido há ${Math.abs(alerta.dias_restantes)} dias`
                                                : `Vence em ${alerta.dias_restantes} dias`
                                            }
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {alertasDocumentos.total > 5 && (
                            <Link to="/convoys" className="block mt-3 text-center text-sm text-cyan-400 hover:text-cyan-300">
                                Ver todos os {alertasDocumentos.total} alertas →
                            </Link>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Alertas Desempenho PLR */}
            {alertasPLR.length > 0 && (
                <Card className="bg-amber-950/30 border-amber-900/50 rounded-sm" data-testid="card-alertas-plr">
                    <CardHeader className="border-b border-amber-900/30 p-4">
                        <CardTitle className="font-heading text-lg text-amber-400 flex items-center gap-2">
                            <Trophy className="w-5 h-5" />
                            Alertas de Desempenho PLR
                            <span className="ml-2 px-2 py-0.5 text-[10px] font-mono uppercase bg-amber-900/40 text-amber-300 rounded-sm">
                                {alertasPLR.length} embarcacao(oes) sem meta
                            </span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {alertasPLR.map((a) => (
                                <div
                                    key={a.id}
                                    className="flex items-start justify-between gap-3 p-3 bg-red-950/30 border-l-2 border-red-500 rounded-sm"
                                    data-testid={`alerta-plr-${a.id}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <p className="text-sm text-white font-semibold">{a.embarcacao_nome}</p>
                                            <span className="text-[10px] font-mono uppercase text-slate-400 capitalize">
                                                {a.tipo_embarcacao}
                                            </span>
                                            <span className="text-[10px] font-mono text-slate-500">
                                                ({a.periodo})
                                            </span>
                                        </div>
                                        {(a.motivos || []).map((m, i) => (
                                            <p key={i} className="text-xs text-red-300 ml-1">
                                                • {m}
                                            </p>
                                        ))}
                                    </div>
                                    <button
                                        onClick={() => marcarAlertaPLRLido(a.id)}
                                        className="text-slate-500 hover:text-white p-1 hover:bg-slate-800/50 rounded-sm"
                                        title="Marcar como lido"
                                        data-testid={`btn-marcar-lido-${a.id}`}
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <Link to="/desempenho-plr" className="block mt-3 text-center text-sm text-cyan-400 hover:text-cyan-300">
                            Ver Desempenho PLR completo →
                        </Link>
                    </CardContent>
                </Card>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm card-hover">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-slate-500 font-mono uppercase tracking-wider">Embarcações</p>
                                <p className="text-3xl font-heading font-bold text-white mt-1">
                                    {kpis?.comboios?.total || 0}
                                </p>
                                <p className="text-xs text-emerald-400 font-mono mt-1">
                                    {kpis?.comboios?.navegando || 0} navegando
                                </p>
                            </div>
                            <div className="p-3 bg-cyan-500/10 rounded-sm">
                                <Ship className="w-6 h-6 text-cyan-400" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-slate-900/50 border-slate-800 rounded-sm card-hover">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-slate-500 font-mono uppercase tracking-wider">ROO Abertos</p>
                                <p className="text-3xl font-heading font-bold text-white mt-1">
                                    {kpis?.roos?.abertos || 0}
                                </p>
                                <p className="text-xs text-amber-400 font-mono mt-1">
                                    {kpis?.roos?.criticos || 0} críticos
                                </p>
                            </div>
                            <div className="p-3 bg-amber-500/10 rounded-sm">
                                <AlertTriangle className="w-6 h-6 text-amber-400" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-slate-900/50 border-slate-800 rounded-sm card-hover">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-slate-500 font-mono uppercase tracking-wider">Sinistros</p>
                                <p className="text-3xl font-heading font-bold text-white mt-1">
                                    {kpis?.sinistros?.abertos || 0}
                                </p>
                                <p className="text-xs text-slate-400 font-mono mt-1">
                                    {kpis?.sinistros?.total || 0} total
                                </p>
                            </div>
                            <div className="p-3 bg-red-500/10 rounded-sm">
                                <Flame className="w-6 h-6 text-red-400" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-slate-900/50 border-slate-800 rounded-sm card-hover">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-slate-500 font-mono uppercase tracking-wider">PA Ativos</p>
                                <p className="text-3xl font-heading font-bold text-white mt-1">
                                    {kpis?.pas?.abertos || 0}
                                </p>
                                <p className="text-xs text-slate-400 font-mono mt-1">
                                    {kpis?.pas?.total || 0} total
                                </p>
                            </div>
                            <div className="p-3 bg-emerald-500/10 rounded-sm">
                                <Phone className="w-6 h-6 text-emerald-400" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* ROO by Criticality */}
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardHeader className="border-b border-slate-800 p-4">
                        <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                            <Activity className="w-4 h-4 text-cyan-400" />
                            ROO por Criticidade
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                        <div className="h-64 w-full">
                            <ResponsiveContainer width="100%" height={256}>
                                <BarChart data={formatChartData(chartData?.roos_by_criticality, criticidadeLabels)} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                                    <XAxis 
                                        dataKey="name" 
                                        tick={{ fill: '#64748b', fontSize: 11 }}
                                        axisLine={{ stroke: '#1e293b' }}
                                    />
                                    <YAxis 
                                        tick={{ fill: '#64748b', fontSize: 11 }}
                                        axisLine={{ stroke: '#1e293b' }}
                                    />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#0f172a', 
                                            border: '1px solid #1e293b',
                                            borderRadius: '2px'
                                        }}
                                        labelStyle={{ color: '#f8fafc' }}
                                    />
                                    <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                                        {formatChartData(chartData?.roos_by_criticality, criticidadeLabels).map((entry, index) => (
                                            <Cell 
                                                key={`cell-${index}`} 
                                                fill={criticidadeColors[Object.keys(criticidadeLabels).find(k => criticidadeLabels[k] === entry.name)] || COLORS.cyan} 
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Convoys by Status */}
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardHeader className="border-b border-slate-800 p-4">
                        <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                            <Navigation className="w-4 h-4 text-cyan-400" />
                            Comboios por Status
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                        <div className="h-64 w-full">
                            <ResponsiveContainer width="100%" height={256}>
                                <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                                    <Pie
                                        data={formatChartData(chartData?.convoys_by_status, statusLabels)}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {formatChartData(chartData?.convoys_by_status, statusLabels).map((entry, index) => (
                                            <Cell 
                                                key={`cell-${index}`} 
                                                fill={statusColors[Object.keys(statusLabels).find(k => statusLabels[k] === entry.name)] || COLORS.cyan} 
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#0f172a', 
                                            border: '1px solid #1e293b',
                                            borderRadius: '2px'
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex flex-wrap justify-center gap-4 mt-2">
                            {Object.entries(statusLabels).map(([key, label]) => (
                                <div key={key} className="flex items-center gap-2">
                                    <div 
                                        className="w-3 h-3 rounded-sm"
                                        style={{ backgroundColor: statusColors[key] }}
                                    />
                                    <span className="text-xs text-slate-400">{label}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Lists Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Recent Convoys */}
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardHeader className="border-b border-slate-800 p-4">
                        <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                            <Ship className="w-4 h-4 text-cyan-400" />
                            Comboios Recentes
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {convoys.length === 0 ? (
                            <p className="text-slate-500 text-center py-8">Nenhum comboio cadastrado</p>
                        ) : (
                            <div className="divide-y divide-slate-800">
                                {convoys.map((convoy) => (
                                    <div key={convoy.id} className="p-4 hover:bg-slate-900/30 transition-colors">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className={`status-indicator status-${convoy.status}`} />
                                                <div>
                                                    <p className="text-sm font-medium text-white">{convoy.nome}</p>
                                                    <p className="text-xs text-slate-500 font-mono">{convoy.codigo}</p>
                                                </div>
                                            </div>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${convoy.status === 'navegando' ? 'badge-success' : ''}
                                                ${convoy.status === 'atracado' ? 'badge-info' : ''}
                                                ${convoy.status === 'manutencao' ? 'badge-warning' : ''}
                                                ${convoy.status === 'emergencia' ? 'badge-danger' : ''}
                                            `}>
                                                {statusLabels[convoy.status]}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Recent ROOs */}
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardHeader className="border-b border-slate-800 p-4">
                        <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-400" />
                            Últimas Ocorrências
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {recentRoos.length === 0 ? (
                            <p className="text-slate-500 text-center py-8">Nenhuma ocorrência registrada</p>
                        ) : (
                            <div className="divide-y divide-slate-800">
                                {recentRoos.map((roo) => (
                                    <div key={roo.id} className="p-4 hover:bg-slate-900/30 transition-colors">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-medium text-white">{roo.titulo}</p>
                                                <p className="text-xs text-slate-500 font-mono">{roo.codigo}</p>
                                            </div>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${roo.criticidade === 'baixa' ? 'bg-slate-800 text-slate-400 border border-slate-700' : ''}
                                                ${roo.criticidade === 'media' ? 'badge-info' : ''}
                                                ${roo.criticidade === 'alta' ? 'badge-warning' : ''}
                                                ${roo.criticidade === 'critica' ? 'badge-danger' : ''}
                                            `}>
                                                {criticidadeLabels[roo.criticidade]}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default DashboardPage;
